import React from 'react';

/**
 * Trade-In Modal Component
 * Modal for adding trade-in vehicles
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.formData - Form data object
 * @param {Function} props.setFormData - Function to update form data
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 */
export const TradeInModal = ({
  show,
  onClose,
  formData,
  setFormData,
  onSubmit,
  theme,
  colors
}) => {
  if (!show) return null;

  return (
    <div style={{ 
      position: "fixed", 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: "rgba(0, 0, 0, 0.8)", 
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      zIndex: 1000,
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div style={{ 
        position: 'relative',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
        backdropFilter: 'blur(40px) saturate(150%)',
        WebkitBackdropFilter: 'blur(40px) saturate(150%)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: "24px",
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
        padding: "40px", 
        width: "90%", 
        maxWidth: "600px",
        animation: 'scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden'
      }}>
        {/* Top highlight */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
        }} />
        
        {/* Inner glow */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.08), transparent 50%)',
          pointerEvents: 'none'
        }} />
        
        <div style={{ 
          position: 'relative',
          zIndex: 1,
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          marginBottom: "24px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          paddingBottom: "20px"
        }}>
          <h2 style={{ 
            color: "#ffffff", 
            margin: 0,
            fontSize: "28px",
            fontWeight: "600",
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
            textShadow: '0 0 40px rgba(255, 255, 255, 0.1)'
          }}>
            Add Trade-In
          </h2>
          <button 
            onClick={onClose} 
            style={{ 
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: "24px",
              cursor: "pointer",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
          <div>
            <label style={{ display: "block", color: theme.textSecondary, marginBottom: "5px" }}>Year *</label>
            <input 
              type="text" 
              value={formData.year} 
              onChange={(e) => setFormData({ ...formData, year: e.target.value })} 
              style={{ width: "100%", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: theme.text }} 
            />
          </div>
          <div>
            <label style={{ display: "block", color: theme.textSecondary, marginBottom: "5px" }}>Make *</label>
            <input 
              type="text" 
              value={formData.make} 
              onChange={(e) => setFormData({ ...formData, make: e.target.value })} 
              style={{ width: "100%", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: theme.text }} 
            />
          </div>
          <div>
            <label style={{ display: "block", color: theme.textSecondary, marginBottom: "5px" }}>Model *</label>
            <input 
              type="text" 
              value={formData.model} 
              onChange={(e) => setFormData({ ...formData, model: e.target.value })} 
              style={{ width: "100%", padding: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: theme.text }} 
            />
          </div>
        </div>
        <div style={{ 
          position: 'relative',
          zIndex: 1,
          display: "flex", 
          gap: "16px", 
          marginTop: "32px",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          paddingTop: "24px"
        }}>
          <button 
            onClick={onClose} 
            style={{ 
              flex: 1, 
              padding: "14px 24px", 
              background: "rgba(255,255,255,0.1)", 
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)", 
              borderRadius: "14px", 
              color: "#fff", 
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.15)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            Cancel
          </button>
          <button 
            onClick={onSubmit} 
            style={{ 
              flex: 1, 
              padding: "14px 28px", 
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)", 
              border: "none", 
              borderRadius: "14px", 
              color: "#fff", 
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
              letterSpacing: "0.5px",
              transition: "all 0.3s ease",
              boxShadow: "0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              overflow: "hidden"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 0 40px rgba(59, 130, 246, 0.6), 0 8px 25px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = "0 0 20px rgba(59, 130, 246, 0.5), 0 4px 15px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(0.98)";
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

